import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mail, Send, Loader2, Edit3, SkipForward } from "lucide-react";

const ApprovalItem = ({ item, idx, editing, onStartEdit, onCancelEdit, onChangeEdit, isSending, onApprove, onSkip }) => (
  <motion.div
    className="glass-card p-4 border border-white/10"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: idx * 0.03 }}
    data-testid={`approval-item-${idx}`}
  >
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <div className="flex items-center gap-3 flex-wrap">
        <Badge className="bg-ism-teal/20 text-ism-teal border border-ism-teal/30 text-xs">Step {item.stepNum}</Badge>
        {item.dayLabel && <span className="text-xs text-gray-600">{item.dayLabel}</span>}
        <span className="text-sm text-white font-medium">{item.contactName}</span>
        <span className="text-xs text-gray-500">{item.companyName}</span>
      </div>
      <span className="text-xs text-gray-600 font-mono">{item.contactEmail}</span>
    </div>

    {editing ? (
      <div className="space-y-2 mb-3">
        <Input value={editing.subject} onChange={(e) => onChangeEdit({ subject: e.target.value })} className="bg-white/5 border-white/10 text-white text-sm" placeholder="Subject" data-testid={`edit-subject-${idx}`} />
        <Textarea value={editing.body} onChange={(e) => onChangeEdit({ body: e.target.value })} rows={6} className="bg-white/5 border-white/10 text-white text-sm resize-none" data-testid={`edit-body-${idx}`} />
      </div>
    ) : (
      <div className="mb-3">
        <p className="text-sm text-gray-300 font-medium mb-1">Subject: {item.subject}</p>
        <p className="text-sm text-gray-400 whitespace-pre-wrap leading-relaxed">{item.body}</p>
      </div>
    )}

    <div className="flex items-center gap-2 pt-2 border-t border-white/5">
      <Button onClick={onApprove} disabled={isSending} size="sm" className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium" data-testid={`approve-send-${idx}`}>
        {isSending ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Sending...</> : <><Send className="h-3 w-3 mr-1" /> Approve & Send</>}
      </Button>
      {editing ? (
        <Button onClick={onCancelEdit} size="sm" variant="ghost" className="text-gray-400 hover:text-white text-xs" data-testid={`cancel-edit-${idx}`}>Cancel Edit</Button>
      ) : (
        <Button onClick={onStartEdit} size="sm" variant="ghost" className="text-gray-400 hover:text-white text-xs" data-testid={`edit-btn-${idx}`}>
          <Edit3 className="h-3 w-3 mr-1" /> Edit
        </Button>
      )}
      <Button onClick={onSkip} size="sm" variant="ghost" className="text-gray-500 hover:text-red-400 text-xs ml-auto" data-testid={`skip-prospect-${idx}`}>
        <SkipForward className="h-3 w-3 mr-1" /> Skip Prospect
      </Button>
    </div>
  </motion.div>
);

export const ApprovalQueue = ({ queue, onApprove, onSkip }) => {
  const [editing, setEditing] = useState(null); // { key, subject, body }
  const [sendingKey, setSendingKey] = useState(null);

  const approve = async (item, key) => {
    setSendingKey(key);
    const edits = editing?.key === key ? { editedSubject: editing.subject, editedBody: editing.body } : {};
    const ok = await onApprove(item, edits);
    if (ok) setEditing(null);
    setSendingKey(null);
  };

  return (
    <div data-testid="approval-queue">
      <h2 className="text-lg text-white font-medium mb-3 flex items-center gap-2">
        <Mail className="h-5 w-5 text-ism-teal" />
        Approval Queue ({queue.length} emails)
      </h2>
      <div className="space-y-3">
        {queue.map((item, idx) => {
          const key = `${item.prospectId}-${item.stepNum}`;
          return (
            <ApprovalItem
              key={key}
              item={item}
              idx={idx}
              editing={editing?.key === key ? editing : null}
              onStartEdit={() => setEditing({ key, subject: item.subject, body: item.body })}
              onCancelEdit={() => setEditing(null)}
              onChangeEdit={(patch) => setEditing((prev) => ({ ...prev, ...patch }))}
              isSending={sendingKey === key}
              onApprove={() => approve(item, key)}
              onSkip={() => onSkip(item.prospectId)}
            />
          );
        })}
      </div>
    </div>
  );
};
